import _ from 'lodash';

import {
  AiFormFieldReviewState,
  AiProcessStepTerminalStatuses,
  LoadStatus,
  RUNS_DEFAULT_PAGE_SIZE,
} from 'constants/aiProcessRuns';

const REVIEWED_FIELD_STATES = new Set([
  AiFormFieldReviewState.AGREED,
  AiFormFieldReviewState.OVERRIDDEN,
  AiFormFieldReviewState.FLAGGED,
]);

const defaultFilters = Object.freeze({
  status: null,
  processId: null,
  startedAfter: null,
  startedBefore: null,
  skip: 0,
  take: RUNS_DEFAULT_PAGE_SIZE,
});

export const ensureFilters = current => ({ ...defaultFilters, ...(current ?? {}) });

const isPaginationKey = key => key === 'skip' || key === 'take';

export const mergeFiltersForOwner = (current, partial) => {
  const base = ensureFilters(current);

  if (_.isNil(partial)) {
    return base;
  }

  const hasNonPaginationKey = Object.keys(partial).some(key => !isPaginationKey(key));

  return {
    ...base,
    ...partial,
    skip: hasNonPaginationKey ? 0 : (partial.skip ?? base.skip),
  };
};

export const toRecord = items => items.reduce((acc, item) => ({ ...acc, [item.id]: item }), {});

export const upsertExecution = (state, execution, ownerId) => {
  const nextRunsById = {
    ...state.runsById,
    [execution.id]: { ...state.runsById[execution.id], ...execution },
  };

  if (!ownerId) {
    return { runsByOwner: state.runsByOwner, runsById: nextRunsById };
  }

  const existing = state.runsByOwner[ownerId];
  const existingIds = existing?.ids ?? [];
  const hasId = existingIds.includes(execution.id);
  const nextIds = hasId ? existingIds : [execution.id, ...existingIds];
  const nextTotal = hasId
    ? (existing?.totalCount ?? nextIds.length)
    : (existing?.totalCount ?? 0) + 1;

  return {
    runsByOwner: {
      ...state.runsByOwner,
      [ownerId]: {
        ids: nextIds,
        totalCount: nextTotal,
        status: existing?.status ?? LoadStatus.IDLE,
      },
    },
    runsById: nextRunsById,
  };
};

export const sortStepIdsByStepNumber = (ids, stepsById) =>
  _.sortBy(ids, id =>
    _.isFinite(stepsById[id]?.stepNumber) ? stepsById[id].stepNumber : Infinity,
  );

export const upsertStep = (state, step) => {
  const executionId = step.executionId ?? step.aiProcessExecutionId;
  const nextStepsById = {
    ...state.stepsById,
    [step.id]: { ...state.stepsById[step.id], ...step },
  };

  if (!executionId) {
    return { stepsByRunId: state.stepsByRunId, stepsById: nextStepsById };
  }

  const existing = state.stepsByRunId[executionId];
  const existingIds = existing?.ids ?? [];
  const nextIds = sortStepIdsByStepNumber(
    existingIds.includes(step.id) ? existingIds : [...existingIds, step.id],
    nextStepsById,
  );

  return {
    stepsByRunId: {
      ...state.stepsByRunId,
      [executionId]: {
        ids: nextIds,
        status: existing?.status ?? LoadStatus.IDLE,
      },
    },
    stepsById: nextStepsById,
  };
};

export const mergeFormFieldEvent = (form, event) => {
  if (!form || !event?.fieldKey) {
    return form;
  }

  const existingFields = form.fields ?? [];
  const index = existingFields.findIndex(field => field.fieldKey === event.fieldKey);
  const patch = _.omitBy(
    {
      value: event.value,
      provenanceSource: event.provenanceSource,
      confidence: event.confidence,
      reviewState: event.reviewState,
    },
    _.isUndefined,
  );

  if (index === -1) {
    return { ...form, fields: [...existingFields, { fieldKey: event.fieldKey, ...patch }] };
  }

  const nextFields = existingFields.map((field, fieldIndex) => {
    if (fieldIndex !== index) {
      return field;
    }

    const merged = { ...field, ...patch };
    const isValueChanged = !_.isUndefined(event.value) && !_.isEqual(event.value, field.value);
    const wasReviewed = REVIEWED_FIELD_STATES.has(field.reviewState);

    // A re-fill that swaps the value out from under a human decision must not
    // be silent. Stamp the drift so the surface can flag "changed since you
    // reviewed" and keep the value the reviewer actually signed off on.
    if (isValueChanged && wasReviewed) {
      return {
        ...merged,
        changedAfterReview: true,
        previousReviewedValue: field.value ?? null,
      };
    }

    return merged;
  });

  return { ...form, fields: nextFields };
};

export const appendMessageToStep = (state, stepExecutionId, message) => {
  // LEN-26: once a step is terminal, its forced completion refetch
  // (loadStepMessagesAction force) is the authoritative transcript. A late live
  // row (a step_message_appended that lands AFTER that refetch resolved) would
  // otherwise append a SECOND copy of a tool call — the live row is keyed by the
  // tool-call id while the persisted row carries a fresh server id, so the
  // id-dedupe below can't absorb it. Drop live appends to an already-terminal
  // step; the canonical reload owns its transcript.
  if (AiProcessStepTerminalStatuses.has(state.stepsById[stepExecutionId]?.status)) {
    return { messagesByStepId: state.messagesByStepId, messagesById: state.messagesById };
  }

  const nextMessagesById = {
    ...state.messagesById,
    [message.id]: { ...state.messagesById[message.id], ...message },
  };
  const existing = state.messagesByStepId[stepExecutionId];
  const existingIds = existing?.ids ?? [];
  const nextIds = existingIds.includes(message.id) ? existingIds : [...existingIds, message.id];

  return {
    messagesByStepId: {
      ...state.messagesByStepId,
      [stepExecutionId]: {
        ids: nextIds,
        status: existing?.status ?? LoadStatus.IDLE,
      },
    },
    messagesById: nextMessagesById,
  };
};
